import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Send } from 'lucide-react';

const API_BASE = '/api';

export function SocialEdit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [socialKey, setSocialKey] = useState(searchParams.get('key'));

  const [loading, setLoading] = useState(true);
  const [postText, setPostText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (socialKey) {
      fetchSocialData(socialKey);
    } else {
      // No key in URL - fetch latest
      fetchLatestSocialKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialKey]);

  const fetchLatestSocialKey = async () => {
    try {
      const response = await fetch(`${API_BASE}/social.php`, { credentials: 'include' });
      if (response.status === 401) {
        navigate('/');
        return;
      }
      const result = await response.json();
      if (result.success && result.key) {
        console.log('📅 Latest social key:', result.key);
        setSocialKey(result.key);
        // Update URL with the key
        setSearchParams({ key: result.key });
        toast.success('Załadowano najnowszy social post');
      } else {
        toast.error(result.error || 'Nie znaleziono social post');
        setLoading(false);
      }
    } catch (error) {
      toast.error('Błąd pobierania najnowszego social post');
      setLoading(false);
    }
  };

  useEffect(() => {
    setCharCount(postText.length);
  }, [postText]);

  const fetchSocialData = async (key) => {
    try {
      const response = await fetch(`${API_BASE}/preview.php?key=${key}`, { credentials: 'include' });
      if (response.status === 401) {
        navigate('/');
        return;
      }
      const result = await response.json();
      if (result.success) {
        const data = result.data;
        setPostText(data.post || '');
        setImageUrl(data.obraz || '');
        toast.success('Social post załadowany');
      } else {
        toast.error('Nie znaleziono social post');
      }
    } catch (error) {
      toast.error('Błąd pobierania social post');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateImage = async () => {
    setIsRegenerating(true);
    try {
      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'generate-social',
          payload: {
            social_key: socialKey,
            post_text: postText
          }
        })
      });

      const result = await response.json();
      console.log('🔄 Regenerate response:', result);

      if (result.status === 'processing') {
        toast.success('⏳ Generowanie nowej grafiki... (1-2 min)');

        // Poll for new image
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(`https://n8n.studiogo.pl/webhook/check-draft-status?key=${encodeURIComponent(socialKey)}`);
            const statusData = await statusResponse.json();

            if (statusData.ready) {
              clearInterval(pollInterval);
              toast.success('✨ Grafika wygenerowana! Odświeżam...');
              // Reload page to show new image
              window.location.reload();
            }
          } catch (error) {
            console.error('Regenerate polling error:', error);
          }
        }, 10000);
      } else {
        toast.error('Błąd: ' + (result.message || 'Nieznany błąd'));
        setIsRegenerating(false);
      }
    } catch (error) {
      toast.error('Błąd: ' + error.message);
      setIsRegenerating(false);
    }
  };

  const handlePublishToPostiz = async () => {
    setIsPublishing(true);
    try {
      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'publish-postiz',
          payload: {
            social_key: socialKey,
            post_text: postText,
            image_url: imageUrl
          }
        })
      });

      const result = await response.json();
      console.log('📤 Publish response:', result);

      if (result.success) {
        const platforms = {
          'cmbhbnnmi0001mub38pr6m02s': 'LinkedIn',
          'cmbmi98lq0001o29gt0xp1jza': 'Instagram',
          'cmbmhivha0003qehe1t5uwan2': 'Facebook',
          'cmbhxdtip0001o3ajgnz5islx': 'YouTube',
          'cmbpb6wyi0001n99t7fgiuwsi': 'Threads'
        };

        const postsList = result.posts.map(p =>
          `${platforms[p.integration] || 'Unknown'}: ${p.postId}`
        ).join('\n');

        toast.success(`✅ ${result.message}\n\n${postsList}`, { duration: 10000 });

        // Navigate back to dashboard
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        toast.error('Błąd: ' + (result.message || 'Błąd publikacji'));
      }
    } catch (error) {
      toast.error('Błąd: ' + error.message);
    } finally {
      setIsPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-semibold text-muted-foreground">Ładowanie social post...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-4 lg:p-6">
      {/* Header */}
      <header className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <button onClick={() => navigate('/')} className="text-sm text-primary hover:underline mb-2 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Powrót do Dashboard
            </button>
            <h2 className="text-2xl lg:text-3xl font-bold">🎨 Edycja Social Media</h2>
            <p className="text-muted-foreground mt-1">
              Klucz: <code className="bg-muted px-2 py-0.5 rounded text-sm break-all">{socialKey}</code>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
            <button
              onClick={handleRegenerateImage}
              disabled={isRegenerating}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-50 w-full sm:w-auto"
            >
              <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
              {isRegenerating ? 'Generowanie...' : 'Regeneruj Grafikę'}
            </button>
            <button
              onClick={handlePublishToPostiz}
              disabled={isPublishing}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 w-full sm:w-auto"
            >
              <Send className="w-4 h-4" />
              {isPublishing ? 'Publikowanie...' : 'Zaplanuj w Postiz'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Text Editor */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 lg:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">📝 Treść Posta</h2>
              <p className="text-sm text-gray-600 mt-1">Edytuj przed publikacją</p>
            </div>

            <div className="p-4 lg:p-6">
              <textarea
                value={postText}
                onChange={(e) => setPostText(e.target.value)}
                className="w-full h-[300px] lg:h-[500px] p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors resize-none font-sans text-sm"
                placeholder="Treść posta..."
              />

              <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                <span>{charCount} znaków</span>
                <span className="text-xs">LinkedIn: max 3000 | Threads: max 500/komentarz</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Image Preview */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-4">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-4 lg:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">🖼️ Grafika</h2>
              <p className="text-sm text-gray-600 mt-1">Wygenerowana przez AI</p>
            </div>

            <div className="p-4 lg:p-6">
              <div className="relative group">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    className="w-full h-auto rounded-lg shadow-md hover:shadow-xl transition-shadow object-contain max-h-[500px] bg-gray-100"
                    alt="Social media post"
                  />
                ) : (
                  <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                    Brak obrazu
                  </div>
                )}

                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg pointer-events-none"></div>
              </div>

              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">Kliknij "🔄 Regeneruj Grafikę" aby wygenerować nowy obraz</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
